// Bilingual content for the Bambara guide page (src/components/GuideBambara.tsx)
//
// IMPORTANT: This page contains real Bambara-language example words, phrases and
// grammatical particles (e.g. "bá", "N bɛ dun", "ka", "ye", "bɛ", "tɛ", "ma", table
// entries in the "ba" columns, etc.). Those forms are NOT translated and are identical
// in the `fr` and `en` entries below — only the surrounding French prose explaining them
// changes between languages.

export interface GuideBambaraGreetingRow {
  label: string; // French or English gloss
  ba: string;    // Bambara form — identical across languages
  pron: string;  // Phonetic pronunciation guide — identical across languages
}

export interface GuideBambaraVocabRow {
  label: string; // French or English gloss
  ba: string;    // Bambara form (may contain a short French/English gloss in parentheses)
}

export interface GuideBambaraParticle {
  term: string; // Bambara particle — identical across languages
  desc: string; // French or English description
}

export interface GuideBambaraContent {
  docTitle: string;
  breadcrumbGuides: string;
  pageTitle: string;
  intro: string;

  whatIsTitle: string;
  whatIsP1Intro: string;
  whatIsP1Meaning: string;
  whatIsP1Vehicular: string;
  whatIsP1After: string;
  whatIsP2Part1: string;
  whatIsP2Manding: string;
  whatIsP2Part2: string;
  whatIsP2Ghana: string;
  whatIsP2Part3: string;
  whatIsP2Mali: string;
  whatIsP2Part4: string;
  whatIsP2Songhai: string;
  whatIsP2Part5: string;
  whatIsP3Part1: string;
  whatIsP3Segou: string;
  whatIsP3Part2: string;

  tonesTitle: string;
  tonesP1Part1: string;
  tonesP1Strong: string;
  tonesP1Part2: string;
  toneHighLabel: string;
  toneHighDesc: string;
  toneHighExampleLabel: string;
  toneHighGloss1: string;
  toneHighVs: string;
  toneHighGloss2: string;
  toneLowLabel: string;
  toneLowDesc: string;
  tonesP2Part1: string;
  tonesP2Part2: string;

  grammarTitle: string;
  grammarP1Part1: string;
  grammarP1Strong1: string;
  grammarP1Part2: string;
  grammarP1Strong2: string;
  grammarP1Part3: string;
  particles: GuideBambaraParticle[];
  grammarP2Label: string;
  grammarP2Gloss1: string;
  grammarP2Gloss2: string;
  grammarP2Gloss3: string;
  grammarP2VerbGloss: string;

  greetingsTitle: string;
  tableHeaderLabel: string;
  tableHeaderBa: string;
  tableHeaderPron: string;
  greetingsRows: GuideBambaraGreetingRow[];

  vocabTitle: string;
  vocabRows: GuideBambaraVocabRow[];

  cultureTitle: string;
  cultureP1Part1: string;
  cultureP1Strong: string;
  cultureP1Part2: string;
  cultureP2Part1: string;
  cultureP2Strong1: string;
  cultureP2Part2: string;
  cultureP2Strong2: string;
  cultureP2Part3: string;
  cultureP2Strong3: string;
  cultureP2Part4: string;
  cultureP3Part1: string;
  cultureP3Strong: string;
  cultureP3Part2: string;

  ctaTitle: string;
  ctaText: string;
  ctaLearnButton: string;
  ctaWolofButton: string;
  ctaSwahiliButton: string;

  footerHome: string;
  footerAbout: string;
  footerWolof: string;
  footerSwahili: string;
}

export const GUIDE_BAMBARA_CONTENT: Record<'fr' | 'en', GuideBambaraContent> = {
  fr: {
    docTitle: 'Apprendre le Bambara — Guide complet | JogoLinga',
    breadcrumbGuides: 'Guides des langues',
    pageTitle: 'Apprendre le Bambara — Guide complet pour débutants',
    intro: "Le bambara est la langue véhiculaire du Mali, parlée par 15 à 40 millions de personnes en Afrique de l'Ouest. Ce guide vous introduit à cette langue tonale héritée des grands empires de l'Ouest africain.",

    whatIsTitle: "Qu'est-ce que le Bambara ?",
    whatIsP1Intro: 'Le bambara (ou ',
    whatIsP1Meaning: " — littéralement \"la langue des Bamana\") est la principale langue de communication au Mali. Bien que le bambara ne soit parlé comme langue maternelle que par environ 4 millions de personnes (l'ethnie bambara), il est utilisé comme ",
    whatIsP1Vehicular: 'langue véhiculaire',
    whatIsP1After: " par plus de 15 millions de Maliens de toutes origines, et compris dans une large zone allant du Burkina Faso à la Guinée, de la Côte d'Ivoire au Sénégal.",
    whatIsP2Part1: "Le bambara appartient à la famille ",
    whatIsP2Manding: 'manding',
    whatIsP2Part2: " (Niger-Congo), un groupe de langues apparentées qui comprend le mandinka, le dioula, le soninké et le malinké. Cette famille linguistique est associée à l'héritage des grands empires médiévaux de l'Afrique de l'Ouest : l'",
    whatIsP2Ghana: 'Empire du Ghana',
    whatIsP2Part3: " (IVe-XIe siècle), l'",
    whatIsP2Mali: 'Empire du Mali',
    whatIsP2Part4: ' (XIIIe-XVe siècle) et l\'',
    whatIsP2Songhai: 'Empire Songhaï',
    whatIsP2Part5: " (XIVe-XVIe siècle). Parler bambara, c'est donc accéder à un héritage historique d'une profondeur remarquable.",
    whatIsP3Part1: "Plus récemment, le bambara est la langue de l'",
    whatIsP3Segou: 'Empire bambara de Ségou',
    whatIsP3Part2: " (XVIIe-XIXe siècle), un royaume prospère fondé par Biton Coulibaly sur les rives du fleuve Niger. Ségou reste aujourd'hui un centre culturel majeur du Mali, ville du grand Festival sur le Niger qui célèbre les arts et traditions de l'Afrique de l'Ouest.",

    tonesTitle: 'Le système tonal — La caractéristique clé du Bambara',
    tonesP1Part1: 'Le bambara est une ',
    tonesP1Strong: 'langue à tons',
    tonesP1Part2: ' : la hauteur musicale avec laquelle on prononce une syllabe change le sens du mot. Il existe deux tons principaux en bambara :',
    toneHighLabel: 'Ton haut (´)',
    toneHighDesc: 'voyelle prononcée avec une hauteur musicale élevée.',
    toneHighExampleLabel: 'Exemple :',
    toneHighGloss1: '(mère, fleuve)',
    toneHighVs: 'vs',
    toneHighGloss2: '(chèvre)',
    toneLowLabel: 'Ton bas (sans accent)',
    toneLowDesc: "voyelle prononcée avec une hauteur plus grave. Les tonèmes bas changent souvent le sens lexical mais aussi grammatical d'un mot.",
    tonesP2Part1: "Pour un francophone, les tons constituent le principal défi du bambara. La bonne nouvelle : ils s'acquièrent par l'écoute répétée et l'immersion musicale. La musique bambara — ",
    tonesP2Part2: ' (musique des griots), kora, balafon, djembé — est une ressource pédagogique extraordinaire pour intérioriser les tons naturellement.',

    grammarTitle: 'Grammaire de base — Structure de la phrase en Bambara',
    grammarP1Part1: "Le bambara suit l'ordre ",
    grammarP1Strong1: 'SVO',
    grammarP1Part2: ' (Sujet - Verbe - Objet), comme le français. Cependant, la conjugaison fonctionne très différemment : au lieu de modifier le verbe (comme "je mange / je mangeais / j\'ai mangé"), le bambara utilise des ',
    grammarP1Strong2: 'particules verbales',
    grammarP1Part3: " postposées au sujet pour indiquer le temps, l'aspect et le mode :",
    particles: [
      { term: 'ka', desc: 'infinitif / habitude présente' },
      { term: 'ye', desc: 'passé accompli (affirmatif)' },
      { term: 'bɛ', desc: 'présent en cours / futur proche' },
      { term: 'tɛ', desc: 'négation présente' },
      { term: 'ma', desc: 'négation passée' },
    ],
    grammarP2Label: 'Exemples : ',
    grammarP2Gloss1: '(Je mange — en ce moment), ',
    grammarP2Gloss2: "(J'ai mangé), ",
    grammarP2Gloss3: '(Je ne mange pas). Le verbe ',
    grammarP2VerbGloss: "(manger) reste invariable ; c'est la particule qui change.",

    greetingsTitle: 'Salutations et expressions indispensables',
    tableHeaderLabel: 'Français',
    tableHeaderBa: 'Bambara',
    tableHeaderPron: 'Prononciation',
    greetingsRows: [
      { label: 'Bonjour (matin)', ba: 'I ni sogoma', pron: 'i ni SO-go-ma' },
      { label: 'Bonjour (après-midi)', ba: 'I ni tile', pron: 'i ni TI-lé' },
      { label: 'Bonsoir', ba: 'I ni wula', pron: 'i ni WU-la' },
      { label: 'Comment vas-tu ?', ba: 'I ka kɛnɛ wa ?', pron: 'i ka KE-nè wa ?' },
      { label: 'Je vais bien', ba: 'N ka kɛnɛ', pron: 'N ka KE-nè' },
      { label: 'Merci', ba: 'Aw ni baara', pron: 'AW ni BAA-ra' },
      { label: "S'il vous plaît", ba: 'Bɛɛ ɲɔgɔn', pron: 'BEE nyo-GON' },
      { label: "Je m'appelle…", ba: 'N tɔgɔ ko…', pron: 'N TO-go ko…' },
      { label: 'Oui', ba: 'Ɔwɔ', pron: 'O-WO' },
      { label: 'Non', ba: 'Ayi', pron: 'A-yi' },
      { label: 'Au revoir', ba: 'Kà taa', pron: 'ka TAA' },
      { label: 'À bientôt', ba: 'An bɛ to', pron: 'AN be TO' },
    ],

    vocabTitle: 'Vocabulaire Bambara — La nourriture et le marché',
    vocabRows: [
      { label: 'Nourriture / Repas', ba: 'Dùmuni' },
      { label: 'Eau', ba: 'Ji' },
      { label: 'Riz', ba: 'Malo' },
      { label: 'Mil / Sorgho', ba: 'Tìɲɛ / Kaba' },
      { label: 'Viande', ba: 'Sogo' },
      { label: 'Poisson', ba: 'Jègè' },
      { label: 'Légume / Sauce', ba: 'Nàajugu / Nàgasi' },
      { label: 'Pain', ba: 'Buruburu' },
      { label: 'Marché', ba: 'Dugu-kɔnɔ (dans la ville)' },
      { label: 'Acheter', ba: 'San' },
      { label: 'Vendre', ba: 'Taa' },
      { label: 'Combien ça coûte ?', ba: 'O juru bɛ di ?' },
    ],

    cultureTitle: 'Culture bambara — Sundiata, Kora et Djembé',
    cultureP1Part1: "La culture manding, dont le bambara est le principal héritier, est associée à l'une des épopées orales les plus célèbres d'Afrique : l'",
    cultureP1Strong: 'Épopée de Sundiata Keïta',
    cultureP1Part2: ". Sundiata (ou Soundjata) est le fondateur légendaire de l'Empire du Mali au XIIIe siècle. Son histoire — un prince handicapé, exilé, qui revient pour vaincre le roi sorcier Soumaoro Kanté — est transmise depuis des siècles par les griots mandingues lors de grandes cérémonies.",
    cultureP2Part1: 'Les instruments de musique manding sont mondialement reconnus. La ',
    cultureP2Strong1: 'kora',
    cultureP2Part2: " est une harpe-luth à 21 cordes fabriquée à partir d'une calebasse. Le ",
    cultureP2Strong2: 'balafon',
    cultureP2Part3: ' est un xylophone à résonateurs. Le ',
    cultureP2Strong3: 'djembé',
    cultureP2Part4: " est le tambour à fût de la famille mandingue, devenu symbole de la percussion africaine dans le monde entier. Écouter ces musiques en pratiquant le bambara est l'une des méthodes d'apprentissage les plus naturelles et les plus agréables.",
    cultureP3Part1: "L'artiste ",
    cultureP3Strong: 'Salif Keïta',
    cultureP3Part2: ', dit "la voix d\'or de l\'Afrique", est l\'un des ambassadeurs les plus connus de la culture manding. Ses chansons en bambara et en malinké ont été entendues dans le monde entier et constituent une ressource musicale idéale pour progresser en bambara.',

    ctaTitle: 'Apprendre le Bambara avec JogoLinga',
    ctaText: 'JogoLinga propose des leçons de bambara avec plus de 500 mots en vocabulaire thématique, des exercices sur le système tonal, des dialogues du quotidien et des chansons pour apprendre en musique. Commencez gratuitement, sans inscription obligatoire.',
    ctaLearnButton: 'Commencer à apprendre le Bambara →',
    ctaWolofButton: 'Guide Wolof',
    ctaSwahiliButton: 'Guide Swahili',

    footerHome: 'Accueil',
    footerAbout: 'À propos',
    footerWolof: 'Guide Wolof',
    footerSwahili: 'Guide Swahili',
  },
  en: {
    docTitle: 'Learn Bambara — Complete Guide | JogoLinga',
    breadcrumbGuides: 'Language guides',
    pageTitle: 'Learn Bambara — A Complete Guide for Beginners',
    intro: "Bambara is the lingua franca of Mali, spoken by 15 to 40 million people across West Africa. This guide introduces you to this tonal language, inherited from the great empires of West Africa.",

    whatIsTitle: 'What is Bambara?',
    whatIsP1Intro: 'Bambara (or ',
    whatIsP1Meaning: ' — literally "the language of the Bamana") is the main language of communication in Mali. Although Bambara is spoken as a mother tongue by only around 4 million people (the Bambara ethnic group), it is used as a ',
    whatIsP1Vehicular: 'lingua franca',
    whatIsP1After: ' by more than 15 million Malians of all backgrounds, and is understood across a wide area stretching from Burkina Faso to Guinea, and from Côte d\'Ivoire to Senegal.',
    whatIsP2Part1: 'Bambara belongs to the ',
    whatIsP2Manding: 'Manding',
    whatIsP2Part2: ' language family (Niger-Congo), a group of related languages that includes Mandinka, Dioula, Soninke, and Malinke. This language family is linked to the legacy of the great medieval empires of West Africa: the ',
    whatIsP2Ghana: 'Ghana Empire',
    whatIsP2Part3: ' (4th–11th century), the ',
    whatIsP2Mali: 'Mali Empire',
    whatIsP2Part4: ' (13th–15th century), and the ',
    whatIsP2Songhai: 'Songhai Empire',
    whatIsP2Part5: ' (14th–16th century). To speak Bambara, then, is to gain access to a historical legacy of remarkable depth.',
    whatIsP3Part1: 'More recently, Bambara was the language of the ',
    whatIsP3Segou: 'Bambara Empire of Ségou',
    whatIsP3Part2: ' (17th–19th century), a prosperous kingdom founded by Biton Coulibaly on the banks of the Niger River. Ségou remains a major cultural center in Mali today, home to the great Festival on the Niger, which celebrates the arts and traditions of West Africa.',

    tonesTitle: "The tonal system — Bambara's key feature",
    tonesP1Part1: 'Bambara is a ',
    tonesP1Strong: 'tonal language',
    tonesP1Part2: ': the pitch used to pronounce a syllable changes the meaning of the word. Bambara has two main tones:',
    toneHighLabel: 'High tone (´)',
    toneHighDesc: 'a vowel pronounced with a high musical pitch.',
    toneHighExampleLabel: 'Example:',
    toneHighGloss1: '(mother, river)',
    toneHighVs: 'vs',
    toneHighGloss2: '(goat)',
    toneLowLabel: 'Low tone (no mark)',
    toneLowDesc: "a vowel pronounced with a lower pitch. Low tones often change not only a word's lexical meaning but its grammatical meaning as well.",
    tonesP2Part1: "For learners coming from a non-tonal language, tone is Bambara's main challenge. The good news: it is acquired through repeated listening and musical immersion. Bambara music — ",
    tonesP2Part2: ' (griot music), kora, balafon, djembé — is an extraordinary learning resource for internalizing tones naturally.',

    grammarTitle: 'Basic grammar — Sentence structure in Bambara',
    grammarP1Part1: 'Bambara follows ',
    grammarP1Strong1: 'SVO',
    grammarP1Part2: ' order (Subject - Verb - Object), like English. However, conjugation works very differently: instead of modifying the verb (as in "I eat / I was eating / I have eaten"), Bambara uses ',
    grammarP1Strong2: 'verbal particles',
    grammarP1Part3: ' placed after the subject to indicate tense, aspect, and mood:',
    particles: [
      { term: 'ka', desc: 'infinitive / present habitual' },
      { term: 'ye', desc: 'completed past (affirmative)' },
      { term: 'bɛ', desc: 'present continuous / near future' },
      { term: 'tɛ', desc: 'present negation' },
      { term: 'ma', desc: 'past negation' },
    ],
    grammarP2Label: 'Examples: ',
    grammarP2Gloss1: '(I am eating — right now), ',
    grammarP2Gloss2: '(I ate), ',
    grammarP2Gloss3: '(I do not eat). The verb ',
    grammarP2VerbGloss: "(to eat) stays unchanged; it's the particle that changes.",

    greetingsTitle: 'Essential greetings and expressions',
    tableHeaderLabel: 'English',
    tableHeaderBa: 'Bambara',
    tableHeaderPron: 'Pronunciation',
    greetingsRows: [
      { label: 'Good morning', ba: 'I ni sogoma', pron: 'i ni SO-go-ma' },
      { label: 'Good afternoon', ba: 'I ni tile', pron: 'i ni TI-lé' },
      { label: 'Good evening', ba: 'I ni wula', pron: 'i ni WU-la' },
      { label: 'How are you?', ba: 'I ka kɛnɛ wa ?', pron: 'i ka KE-nè wa ?' },
      { label: 'I am fine', ba: 'N ka kɛnɛ', pron: 'N ka KE-nè' },
      { label: 'Thank you', ba: 'Aw ni baara', pron: 'AW ni BAA-ra' },
      { label: 'Please', ba: 'Bɛɛ ɲɔgɔn', pron: 'BEE nyo-GON' },
      { label: 'My name is…', ba: 'N tɔgɔ ko…', pron: 'N TO-go ko…' },
      { label: 'Yes', ba: 'Ɔwɔ', pron: 'O-WO' },
      { label: 'No', ba: 'Ayi', pron: 'A-yi' },
      { label: 'Goodbye', ba: 'Kà taa', pron: 'ka TAA' },
      { label: 'See you soon', ba: 'An bɛ to', pron: 'AN be TO' },
    ],

    vocabTitle: 'Bambara vocabulary — Food and the market',
    vocabRows: [
      { label: 'Food / Meal', ba: 'Dùmuni' },
      { label: 'Water', ba: 'Ji' },
      { label: 'Rice', ba: 'Malo' },
      { label: 'Millet / Sorghum', ba: 'Tìɲɛ / Kaba' },
      { label: 'Meat', ba: 'Sogo' },
      { label: 'Fish', ba: 'Jègè' },
      { label: 'Vegetable / Sauce', ba: 'Nàajugu / Nàgasi' },
      { label: 'Bread', ba: 'Buruburu' },
      { label: 'Market', ba: 'Dugu-kɔnɔ (in the town)' },
      { label: 'To buy', ba: 'San' },
      { label: 'To sell', ba: 'Taa' },
      { label: 'How much does it cost?', ba: 'O juru bɛ di ?' },
    ],

    cultureTitle: 'Bambara culture — Sundiata, Kora, and Djembé',
    cultureP1Part1: "Manding culture, of which Bambara is the principal heir, is associated with one of Africa's most famous oral epics: the ",
    cultureP1Strong: 'Epic of Sundiata Keïta',
    cultureP1Part2: '. Sundiata (or Soundiata) is the legendary founder of the Mali Empire in the 13th century. His story — a disabled, exiled prince who returns to defeat the sorcerer-king Soumaoro Kanté — has been passed down for centuries by Manding griots during great ceremonies.',
    cultureP2Part1: 'Manding musical instruments are recognized around the world. The ',
    cultureP2Strong1: 'kora',
    cultureP2Part2: ' is a 21-string harp-lute made from a calabash. The ',
    cultureP2Strong2: 'balafon',
    cultureP2Part3: ' is a resonator xylophone. The ',
    cultureP2Strong3: 'djembé',
    cultureP2Part4: ' is the goblet drum of the Manding family, which has become a symbol of African percussion worldwide. Listening to this music while practicing Bambara is one of the most natural and enjoyable ways to learn.',
    cultureP3Part1: 'The artist ',
    cultureP3Strong: 'Salif Keïta',
    cultureP3Part2: ', known as "the golden voice of Africa," is one of the best-known ambassadors of Manding culture. His songs in Bambara and Malinke have been heard around the world and are an ideal musical resource for improving your Bambara.',

    ctaTitle: 'Learn Bambara with JogoLinga',
    ctaText: 'JogoLinga offers Bambara lessons with more than 500 words of thematic vocabulary, exercises on the tonal system, everyday dialogues, and songs to help you learn through music. Get started for free, no sign-up required.',
    ctaLearnButton: 'Start learning Bambara →',
    ctaWolofButton: 'Wolof Guide',
    ctaSwahiliButton: 'Swahili Guide',

    footerHome: 'Home',
    footerAbout: 'About',
    footerWolof: 'Wolof Guide',
    footerSwahili: 'Swahili Guide',
  },
};
